import {htmlToPlainText} from "../../utils/html-to-plain-text";

export type PublicProfileThemeId =
  | "mono"
  | "ledger"
  | "frost"
  | "lush"
  | "midnight"
  | "velvet"
  | "mint"
  | "terminal"
  | "retro"
  | "brutal";
export type PublicProfileHeaderLayout = "basic-left" | "basic-center" | "full";
export type PublicProfileThemeFont =
  | "Inter"
  | "Lora"
  | "Geist"
  | "Geist Mono"
  | "Libre Baskerville"
  | "Red Rose"
  | "Oswald"
  | "Outfit"
  | "Work Sans"
  | "Space Mono"
  | "Space Grotesk"
  | "Playfair Display"
  | "Merriweather"
  | "Manrope"
  | "Fira Code"
  | "Nunito"
  | "Noto Serif"
  | "JetBrains Mono"
  | "Plus Jakarta Sans"
  | "Archivo"
  | "DM Sans";
export type PublicProfileSurfaceStyle = "filled" | "outlined" | "hard-shadow" | "glass";
export type PublicProfileThemeSettings = {
  backgroundStyle: "solid" | "gradient" | "image";
  backgroundGradientEnd: string;
  backgroundImageUrl: string | null;
  headingFont: PublicProfileThemeFont;
  bodyFont: PublicProfileThemeFont;
  buttonStyle: PublicProfileSurfaceStyle;
  buttonRadius: number;
  blockStyle: PublicProfileSurfaceStyle;
  blockBorderColor: string;
  blockRadius: number;
  blockSpacing: number;
  highlightBackground: string;
  highlightBorderColor: string;
  highlightRadius: number;
  surfaceBackground: string;
  surfaceBorderColor: string;
  surfaceRadius: number;
};
export type PublicProfileBlockType =
  | "offeringCollection"
  | "customLink"
  | "socialLinks"
  | "richText"
  | "image"
  | "divider"
  | "video"
  | "testimonials"
  | "countdown"
  | "embed"
  | "emailSignup"
  | "faq";

export type OfferingKind =
  | "service"
  | "digital-product"
  | "product-service"
  | "live-event"
  | "subscription";

export type OfferingReference = {
  kind: OfferingKind;
  id: number;
};

type PublicProfileBlockBase = {
  id: string;
  visible: boolean;
};

export type OfferingCollectionBlock = PublicProfileBlockBase & {
  type: "offeringCollection";
  title: string;
  description?: string | null;
  collection: "all" | "services" | "store" | "liveEvents";
  source: {mode: "automatic"} | {mode: "selected"; offerings: OfferingReference[]};
  limit: number;
  layout?: "grid" | "list";
  mobileColumns?: 1 | 2;
  buttonLabel?: string | null;
};

export type CustomLinkBlock = PublicProfileBlockBase & {
  type: "customLink";
  title: string | null;
  links: Array<{
    label: string;
    description: string | null;
    url: string;
  }>;
};

export type SocialLinksBlock = PublicProfileBlockBase & {
  type: "socialLinks";
  title: string | null;
};

export type RichTextBlock = PublicProfileBlockBase & {
  type: "richText";
  content: Array<{
    type: "heading" | "paragraph" | "bullet";
    text: string;
  }>;
};

export type ImageBlock = PublicProfileBlockBase & {
  type: "image";
  src: string;
  alt: string;
  caption: string | null;
  linkUrl: string | null;
};

export type DividerBlock = PublicProfileBlockBase & {
  type: "divider";
  style: "solid" | "dashed" | "dotted";
};

export type VideoBlock = PublicProfileBlockBase & {
  type: "video";
  title: string | null;
  embedUrl: string;
};

export type TestimonialsBlock = PublicProfileBlockBase & {
  type: "testimonials";
  title: string | null;
  testimonials: Array<{
    quote: string;
    author: string;
    role: string | null;
    avatarUrl: string | null;
  }>;
};

export type CountdownBlock = PublicProfileBlockBase & {
  type: "countdown";
  title: string;
  targetAt: string;
  expiredLabel: string;
};

export type EmbedBlock = PublicProfileBlockBase & {
  type: "embed";
  title: string | null;
  url: string;
  height: number;
};

export type EmailSignupBlock = PublicProfileBlockBase & {
  type: "emailSignup";
  title: string;
  description: string | null;
  buttonLabel: string;
  formAction: string | null;
  consentLabel: string | null;
};

export type FaqBlock = PublicProfileBlockBase & {
  type: "faq";
  title: string | null;
  items: Array<{
    question: string;
    answer: string;
  }>;
};

export type PublicProfileBlock =
  | OfferingCollectionBlock
  | CustomLinkBlock
  | SocialLinksBlock
  | RichTextBlock
  | ImageBlock
  | DividerBlock
  | VideoBlock
  | TestimonialsBlock
  | CountdownBlock
  | EmbedBlock
  | EmailSignupBlock
  | FaqBlock;

export type PublicProfileDocument = {
  schemaVersion: 1;
  header: {
    layout: PublicProfileHeaderLayout;
    showCoverImage: boolean;
    showAvatar: boolean;
    showBio: boolean;
    showLocation: boolean;
    showProfessionalTitles: boolean;
    showYearsExperience: boolean;
    showSocialLinks: boolean;
    maximumSocialLinks: number;
    yearsExperience?: string;
    professionalTitles?: string[];
    socialLinks?: Array<{name: string; url: string}>;
    coverImage?: {
      url: string;
      alt: string;
      photographerName: string;
      photographerUrl: string;
    } | null;
  };
  blocks: PublicProfileBlock[];
  theme: {
    preset: PublicProfileThemeId;
    settings?: Partial<PublicProfileThemeSettings>;
    overrides: {
      pageBackground: string | null;
      surface: string | null;
      surfaceMuted: string | null;
      text: string | null;
      textMuted: string | null;
      accent: string | null;
      accentText: string | null;
    };
  };
  seo: {
    title: string | null;
    description: string | null;
    canonicalUrl: string | null;
    openGraphImageUrl: string | null;
    faviconUrl: string | null;
    appleTouchIconUrl: string | null;
    noIndex: boolean;
  };
  footer: {
    showCoachliBranding: boolean;
    copyright: string | null;
  };
};

export type PublicProfileValidationIssue = {
  path: string;
  message: string;
  blockId?: string;
};

export const PUBLIC_PROFILE_THEMES: Array<{
  id: PublicProfileThemeId;
  name: string;
  description: string;
  colours: [string, string, string];
  settings: PublicProfileThemeSettings;
}> = [
  {
    id: "mono",
    name: "Mono",
    description: "Editorial black and white",
    colours: ["#f5f5f5", "#ffffff", "#222222"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#e8e8e8",
      backgroundImageUrl: null,
      headingFont: "Inter",
      bodyFont: "Inter",
      buttonStyle: "filled",
      buttonRadius: 10,
      blockStyle: "filled",
      blockBorderColor: "#ebebeb",
      blockRadius: 24,
      blockSpacing: 24,
      highlightBackground: "#f5f5f5",
      highlightBorderColor: "#ebebeb",
      highlightRadius: 24,
      surfaceBackground: "#ffffff",
      surfaceBorderColor: "#ebebeb",
      surfaceRadius: 16,
    },
  },
  {
    id: "ledger",
    name: "Ledger",
    description: "Warm and professional",
    colours: ["#eae6df", "#f9f8f6", "#333333"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#d8d0c4",
      backgroundImageUrl: null,
      headingFont: "Noto Serif",
      bodyFont: "Noto Serif",
      buttonStyle: "filled",
      buttonRadius: 0,
      blockStyle: "filled",
      blockBorderColor: "#f0f0f0",
      blockRadius: 8,
      blockSpacing: 16,
      highlightBackground: "#ffffff",
      highlightBorderColor: "#f0f0f0",
      highlightRadius: 0,
      surfaceBackground: "#ffffff",
      surfaceBorderColor: "#f0f0f0",
      surfaceRadius: 0,
    },
  },
  {
    id: "frost",
    name: "Frost",
    description: "Calm blue clarity",
    colours: ["#dee8fb", "#f1f5f9", "#222222"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#c9d9f5",
      backgroundImageUrl: null,
      headingFont: "Geist",
      bodyFont: "Geist",
      buttonStyle: "glass",
      buttonRadius: 32,
      blockStyle: "glass",
      blockBorderColor: "#ccd9f3",
      blockRadius: 24,
      blockSpacing: 16,
      highlightBackground: "#f1f5f9",
      highlightBorderColor: "#ccd9f3",
      highlightRadius: 24,
      surfaceBackground: "#ffffff",
      surfaceBorderColor: "#ccd9f3",
      surfaceRadius: 16,
    },
  },
  {
    id: "lush",
    name: "Lush",
    description: "Natural and optimistic",
    colours: ["#e8d8ce", "#fdfbf7", "#3d2b1f"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#dcc5b8",
      backgroundImageUrl: null,
      headingFont: "Outfit",
      bodyFont: "Outfit",
      buttonStyle: "filled",
      buttonRadius: 10,
      blockStyle: "filled",
      blockBorderColor: "#edddd4",
      blockRadius: 24,
      blockSpacing: 16,
      highlightBackground: "#e8d8ce",
      highlightBorderColor: "#edddd4",
      highlightRadius: 24,
      surfaceBackground: "#fdfbf7",
      surfaceBorderColor: "#edddd4",
      surfaceRadius: 16,
    },
  },
  {
    id: "midnight",
    name: "Midnight",
    description: "High-contrast dark",
    colours: ["#111111", "#191919", "#f5f5f5"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#242424",
      backgroundImageUrl: null,
      headingFont: "Plus Jakarta Sans",
      bodyFont: "Plus Jakarta Sans",
      buttonStyle: "outlined",
      buttonRadius: 32,
      blockStyle: "outlined",
      blockBorderColor: "#2d2d2d",
      blockRadius: 24,
      blockSpacing: 16,
      highlightBackground: "#191919",
      highlightBorderColor: "#444444",
      highlightRadius: 24,
      surfaceBackground: "#191919",
      surfaceBorderColor: "#444444",
      surfaceRadius: 16,
    },
  },
  {
    id: "velvet",
    name: "Velvet",
    description: "Rich plum and lavender",
    colours: ["#2a1b38", "#3b284f", "#b892ff"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#3b284f",
      backgroundImageUrl: null,
      headingFont: "Lora",
      bodyFont: "Lora",
      buttonStyle: "filled",
      buttonRadius: 32,
      blockStyle: "filled",
      blockBorderColor: "#533370",
      blockRadius: 24,
      blockSpacing: 16,
      highlightBackground: "#2a1b38",
      highlightBorderColor: "#533370",
      highlightRadius: 16,
      surfaceBackground: "#2a1b38",
      surfaceBorderColor: "#533370",
      surfaceRadius: 16,
    },
  },
  {
    id: "mint",
    name: "Mint",
    description: "Fresh, bright and focused",
    colours: ["#a7f3d0", "#f6fffb", "#10b981"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#ebfff6",
      backgroundImageUrl: null,
      headingFont: "Geist",
      bodyFont: "Geist",
      buttonStyle: "filled",
      buttonRadius: 32,
      blockStyle: "filled",
      blockBorderColor: "#cefadb",
      blockRadius: 24,
      blockSpacing: 16,
      highlightBackground: "#ebfff6",
      highlightBorderColor: "#cefadb",
      highlightRadius: 16,
      surfaceBackground: "#ebfff6",
      surfaceBorderColor: "#cefadb",
      surfaceRadius: 16,
    },
  },
  {
    id: "terminal",
    name: "Terminal",
    description: "Command-line green on black",
    colours: ["#0d1117", "#0d1117", "#3fb950"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#161b22",
      backgroundImageUrl: null,
      headingFont: "JetBrains Mono",
      bodyFont: "JetBrains Mono",
      buttonStyle: "outlined",
      buttonRadius: 32,
      blockStyle: "outlined",
      blockBorderColor: "#3fb950",
      blockRadius: 24,
      blockSpacing: 16,
      highlightBackground: "#0d1117",
      highlightBorderColor: "#3fb950",
      highlightRadius: 16,
      surfaceBackground: "#0d1117",
      surfaceBorderColor: "#3fb950",
      surfaceRadius: 16,
    },
  },
  {
    id: "retro",
    name: "Retro",
    description: "Playful purple and pink",
    colours: ["#9d4edd", "#f15bb5", "#ffffff"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#f15bb5",
      backgroundImageUrl: null,
      headingFont: "Fira Code",
      bodyFont: "Fira Code",
      buttonStyle: "hard-shadow",
      buttonRadius: 0,
      blockStyle: "filled",
      blockBorderColor: "#000000",
      blockRadius: 0,
      blockSpacing: 16,
      highlightBackground: "#f15bb5",
      highlightBorderColor: "#000000",
      highlightRadius: 0,
      surfaceBackground: "#f15bb5",
      surfaceBorderColor: "#000000",
      surfaceRadius: 0,
    },
  },
  {
    id: "brutal",
    name: "Brutal",
    description: "Graphic black, white and yellow",
    colours: ["#ffffff", "#ffd700", "#222222"],
    settings: {
      backgroundStyle: "solid",
      backgroundGradientEnd: "#ffd700",
      backgroundImageUrl: null,
      headingFont: "Space Mono",
      bodyFont: "Space Mono",
      buttonStyle: "hard-shadow",
      buttonRadius: 0,
      blockStyle: "outlined",
      blockBorderColor: "#000000",
      blockRadius: 0,
      blockSpacing: 16,
      highlightBackground: "#ffd700",
      highlightBorderColor: "#000000",
      highlightRadius: 0,
      surfaceBackground: "#ffffff",
      surfaceBorderColor: "#000000",
      surfaceRadius: 0,
    },
  },
];

export const PUBLIC_PROFILE_THEME_FONTS: PublicProfileThemeFont[] = [
  "Inter",
  "Lora",
  "Geist",
  "Geist Mono",
  "Libre Baskerville",
  "Red Rose",
  "Oswald",
  "Outfit",
  "Work Sans",
  "Space Mono",
  "Space Grotesk",
  "Playfair Display",
  "Merriweather",
  "Manrope",
  "Fira Code",
  "Nunito",
  "Noto Serif",
  "JetBrains Mono",
  "Plus Jakarta Sans",
  "Archivo",
  "DM Sans",
];

export const PUBLIC_PROFILE_THEME_BLOCK_COLOURS: Record<PublicProfileThemeId, string> = {
  mono: "#f5f5f5",
  ledger: "#ffffff",
  frost: "#ffffff",
  lush: "#e8d8ce",
  midnight: "#191919",
  velvet: "#2a1b38",
  mint: "#ebfff6",
  terminal: "#0d1117",
  retro: "#f15bb5",
  brutal: "#ffffff",
};

export function resolvePublicProfileThemeSettings(
  theme: PublicProfileDocument["theme"]
): PublicProfileThemeSettings {
  const preset = PUBLIC_PROFILE_THEMES.find((item) => item.id === theme.preset)!;
  return {...preset.settings, ...theme.settings};
}

export type PublicProfileBlockLibraryItem = {
  id: string;
  type: PublicProfileBlockType;
  name: string;
  description: string;
  group: "Coachli" | "Content" | "Engagement";
  offeringCollection?: OfferingCollectionBlock["collection"];
};

// Blocks available in the current public-profile customization iteration.
export const PUBLIC_PROFILE_BLOCK_LIBRARY: PublicProfileBlockLibraryItem[] = [
  {
    id: "one-to-one-session",
    type: "offeringCollection",
    name: "1:1 sessions",
    description: "Show your bookable one-to-one sessions",
    group: "Coachli",
    offeringCollection: "services",
  },
  {
    id: "live-events",
    type: "offeringCollection",
    name: "Live Events",
    description: "Show your upcoming live events",
    group: "Coachli",
    offeringCollection: "liveEvents",
  },
  {
    id: "store",
    type: "offeringCollection",
    name: "Store",
    description: "Show products from your store",
    group: "Coachli",
    offeringCollection: "store",
  },
  {
    id: "text",
    type: "richText",
    name: "Text",
    description: "Add headings, paragraphs and bullet points",
    group: "Content",
  },
];

// Kept for later iterations; these implementations remain available but are not
// presented in the block picker yet.
export const DEFERRED_PUBLIC_PROFILE_BLOCK_LIBRARY: PublicProfileBlockLibraryItem[] = [
  {
    id: "social-links",
    type: "socialLinks",
    name: "Social links",
    description: "Reuse the links from your profile",
    group: "Coachli",
  },
  {
    id: "custom-links",
    type: "customLink",
    name: "Custom links",
    description: "A list of calls to action",
    group: "Content",
  },
  {
    id: "image",
    type: "image",
    name: "Image",
    description: "An image with optional caption and link",
    group: "Content",
  },
  {
    id: "video",
    type: "video",
    name: "Video",
    description: "Embed a hosted video",
    group: "Content",
  },
  {
    id: "embed",
    type: "embed",
    name: "Embed",
    description: "Embed an external page or widget",
    group: "Content",
  },
  {
    id: "divider",
    type: "divider",
    name: "Divider",
    description: "Create visual separation",
    group: "Content",
  },
  {
    id: "testimonials",
    type: "testimonials",
    name: "Testimonials",
    description: "Share client quotes and outcomes",
    group: "Engagement",
  },
  {
    id: "faq",
    type: "faq",
    name: "FAQ",
    description: "Answer common questions",
    group: "Engagement",
  },
  {
    id: "countdown",
    type: "countdown",
    name: "Countdown",
    description: "Count down to a launch or event",
    group: "Engagement",
  },
  {
    id: "email-signup",
    type: "emailSignup",
    name: "Email signup",
    description: "Collect email addresses with your provider",
    group: "Engagement",
  },
];

function createBlockId(): string {
  return `block-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createPublicProfileBlock(
  type: PublicProfileBlockType,
  options: {imageUrl?: string | null} = {}
): PublicProfileBlock {
  const id = createBlockId();

  switch (type) {
    case "offeringCollection":
      return {
        id,
        type,
        visible: true,
        title: "Featured offerings",
        description: null,
        collection: "all",
        source: {mode: "automatic"},
        limit: 6,
        layout: "grid",
        mobileColumns: 1,
        buttonLabel: "View offering",
      };
    case "customLink":
      return {
        id,
        type,
        visible: true,
        title: "Useful links",
        links: [{label: "Visit Coachli", description: null, url: "https://coachli.co"}],
      };
    case "socialLinks":
      return {id, type, visible: true, title: "Find me online"};
    case "richText":
      return {
        id,
        type,
        visible: true,
        content: [{type: "paragraph", text: ""}],
      };
    case "image":
      return {
        id,
        type,
        visible: true,
        src: options.imageUrl || "https://coachli.co/favicon.ico",
        alt: "",
        caption: null,
        linkUrl: null,
      };
    case "divider":
      return {id, type, visible: true, style: "solid"};
    case "video":
      return {
        id,
        type,
        visible: true,
        title: "Watch",
        embedUrl: "https://www.youtube-nocookie.com/embed/aqz-KE-bpKQ",
      };
    case "testimonials":
      return {
        id,
        type,
        visible: true,
        title: "What people are saying",
        testimonials: [
          {
            quote: "Add a short quote from a client.",
            author: "Client name",
            role: null,
            avatarUrl: null,
          },
        ],
      };
    case "countdown": {
      const target = new Date();
      target.setDate(target.getDate() + 7);
      return {
        id,
        type,
        visible: true,
        title: "Coming soon",
        targetAt: target.toISOString(),
        expiredLabel: "This event has ended",
      };
    }
    case "embed":
      return {
        id,
        type,
        visible: false,
        title: "Embed",
        url: "https://example.com",
        height: 480,
      };
    case "emailSignup":
      return {
        id,
        type,
        visible: true,
        title: "Join my email list",
        description: "Get occasional updates directly in your inbox.",
        buttonLabel: "Subscribe",
        formAction: null,
        consentLabel: null,
      };
    case "faq":
      return {
        id,
        type,
        visible: true,
        title: "Frequently asked questions",
        items: [
          {
            question: "What can I book with you?",
            answer: "Add a concise answer for your visitors.",
          },
        ],
      };
  }
}

export function duplicatePublicProfileBlock(
  block: PublicProfileBlock
): PublicProfileBlock {
  const duplicate = JSON.parse(JSON.stringify(block)) as PublicProfileBlock;
  return {...duplicate, id: createBlockId()};
}

function isHttpUrl(value: string | null): boolean {
  if (!value) return false;
  try {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

function isOptionalHttpUrl(value: string | null): boolean {
  return value === null || value === "" || isHttpUrl(value);
}

function hasLength(value: string, minimum: number, maximum: number): boolean {
  const length = value.trim().length;
  return length >= minimum && length <= maximum;
}

export function validatePublicProfileDocument(
  document: PublicProfileDocument
): PublicProfileValidationIssue[] {
  const issues: PublicProfileValidationIssue[] = [];
  const add = (path: string, message: string, blockId?: string) =>
    issues.push({path, message, blockId});

  if (document.blocks.length > 50) add("blocks", "A page can contain at most 50 blocks.");
  if (document.header.maximumSocialLinks < 1 || document.header.maximumSocialLinks > 5)
    add("header.maximumSocialLinks", "Show between 1 and 5 social links.");

  const blockIds = new Set<string>();
  document.blocks.forEach((block, blockIndex) => {
    const root = `blocks.${blockIndex}`;
    if (!hasLength(block.id, 1, 80))
      add(`${root}.id`, "The block ID is invalid.", block.id);
    if (blockIds.has(block.id))
      add(`${root}.id`, "Every block needs a unique ID.", block.id);
    blockIds.add(block.id);

    switch (block.type) {
      case "offeringCollection":
        if (!hasLength(block.title, 0, 80))
          add(`${root}.title`, "Use at most 80 characters.", block.id);
        if (block.description && !hasLength(block.description, 0, 100))
          add(`${root}.description`, "Use at most 100 characters.", block.id);
        if (block.layout && !["grid", "list"].includes(block.layout))
          add(`${root}.layout`, "Choose a grid or list layout.", block.id);
        if (block.mobileColumns && ![1, 2].includes(block.mobileColumns))
          add(`${root}.mobileColumns`, "Choose one or two mobile columns.", block.id);
        if (block.buttonLabel && !hasLength(block.buttonLabel, 1, 40))
          add(`${root}.buttonLabel`, "Use at most 40 characters.", block.id);
        if (block.limit < 1 || block.limit > 24)
          add(`${root}.limit`, "Show between 1 and 24 offerings.", block.id);
        if (block.source.mode === "selected") {
          if (block.source.offerings.length > 24)
            add(`${root}.source`, "Select at most 24 offerings.", block.id);
          block.source.offerings.forEach((offering, index) => {
            if (!Number.isInteger(offering.id) || offering.id < 1)
              add(
                `${root}.source.offerings.${index}.id`,
                "Offering IDs must be positive numbers.",
                block.id
              );
          });
        }
        break;
      case "customLink":
        if (block.title && !hasLength(block.title, 0, 80))
          add(`${root}.title`, "Use at most 80 characters.", block.id);
        if (block.links.length < 1 || block.links.length > 12)
          add(`${root}.links`, "Add between 1 and 12 links.", block.id);
        block.links.forEach((link, index) => {
          if (!hasLength(link.label, 1, 80))
            add(`${root}.links.${index}.label`, "Every link needs a label.", block.id);
          if (link.description && !hasLength(link.description, 0, 160))
            add(
              `${root}.links.${index}.description`,
              "Use at most 160 characters.",
              block.id
            );
          if (!isHttpUrl(link.url))
            add(
              `${root}.links.${index}.url`,
              "Enter a complete HTTP or HTTPS URL.",
              block.id
            );
        });
        break;
      case "socialLinks":
        if (block.title && !hasLength(block.title, 0, 80))
          add(`${root}.title`, "Use at most 80 characters.", block.id);
        break;
      case "richText":
        if (block.content.length < 1 || block.content.length > 60)
          add(`${root}.content`, "Add between 1 and 60 text items.", block.id);
        if (
          !block.content.some(
            (node) =>
              node.type !== "heading" && htmlToPlainText(node.text).trim().length > 0
          )
        )
          add(`${root}.content`, "Content is required.", block.id);
        block.content.forEach((node, index) => {
          if (!hasLength(htmlToPlainText(node.text), 0, 2000))
            add(
              `${root}.content.${index}.text`,
              "Use at most 2,000 characters.",
              block.id
            );
        });
        break;
      case "image":
        if (!isHttpUrl(block.src))
          add(`${root}.src`, "Enter a complete image URL.", block.id);
        if (!hasLength(block.alt, 0, 160))
          add(
            `${root}.alt`,
            "Alternative text must be 160 characters or fewer.",
            block.id
          );
        if (block.caption && !hasLength(block.caption, 0, 240))
          add(`${root}.caption`, "Use at most 240 characters.", block.id);
        if (!isOptionalHttpUrl(block.linkUrl))
          add(`${root}.linkUrl`, "Enter a complete HTTP or HTTPS URL.", block.id);
        break;
      case "divider":
        break;
      case "video":
        if (block.title && !hasLength(block.title, 0, 80))
          add(`${root}.title`, "Use at most 80 characters.", block.id);
        if (!isHttpUrl(block.embedUrl))
          add(`${root}.embedUrl`, "Enter a complete video embed URL.", block.id);
        break;
      case "testimonials":
        if (block.title && !hasLength(block.title, 0, 80))
          add(`${root}.title`, "Use at most 80 characters.", block.id);
        if (block.testimonials.length < 1 || block.testimonials.length > 12)
          add(`${root}.testimonials`, "Add between 1 and 12 testimonials.", block.id);
        block.testimonials.forEach((testimonial, index) => {
          if (!hasLength(testimonial.quote, 1, 600))
            add(
              `${root}.testimonials.${index}.quote`,
              "Every testimonial needs a quote.",
              block.id
            );
          if (!hasLength(testimonial.author, 1, 100))
            add(
              `${root}.testimonials.${index}.author`,
              "Every testimonial needs an author.",
              block.id
            );
          if (testimonial.role && !hasLength(testimonial.role, 0, 120))
            add(
              `${root}.testimonials.${index}.role`,
              "Use at most 120 characters.",
              block.id
            );
          if (!isOptionalHttpUrl(testimonial.avatarUrl))
            add(
              `${root}.testimonials.${index}.avatarUrl`,
              "Enter a complete avatar URL.",
              block.id
            );
        });
        break;
      case "countdown":
        if (!hasLength(block.title, 1, 100))
          add(`${root}.title`, "Add a countdown title.", block.id);
        if (Number.isNaN(Date.parse(block.targetAt)))
          add(`${root}.targetAt`, "Choose a valid target date and time.", block.id);
        if (!hasLength(block.expiredLabel, 1, 80))
          add(
            `${root}.expiredLabel`,
            "Add the message shown after the countdown.",
            block.id
          );
        break;
      case "embed":
        if (block.title && !hasLength(block.title, 0, 80))
          add(`${root}.title`, "Use at most 80 characters.", block.id);
        if (!isHttpUrl(block.url))
          add(`${root}.url`, "Enter a complete embed URL.", block.id);
        if (!Number.isInteger(block.height) || block.height < 180 || block.height > 1200)
          add(
            `${root}.height`,
            "Embed height must be between 180 and 1,200 pixels.",
            block.id
          );
        break;
      case "emailSignup":
        if (!hasLength(block.title, 1, 100))
          add(`${root}.title`, "Add a signup title.", block.id);
        if (block.description && !hasLength(block.description, 0, 240))
          add(`${root}.description`, "Use at most 240 characters.", block.id);
        if (!hasLength(block.buttonLabel, 1, 40))
          add(`${root}.buttonLabel`, "Add a button label.", block.id);
        if (!isOptionalHttpUrl(block.formAction))
          add(`${root}.formAction`, "Enter a complete form action URL.", block.id);
        if (block.consentLabel && !hasLength(block.consentLabel, 0, 240))
          add(`${root}.consentLabel`, "Use at most 240 characters.", block.id);
        break;
      case "faq":
        if (block.title && !hasLength(block.title, 0, 80))
          add(`${root}.title`, "Use at most 80 characters.", block.id);
        if (block.items.length < 1 || block.items.length > 20)
          add(`${root}.items`, "Add between 1 and 20 questions.", block.id);
        block.items.forEach((item, index) => {
          if (!hasLength(item.question, 1, 180))
            add(
              `${root}.items.${index}.question`,
              "Every FAQ needs a question.",
              block.id
            );
          if (!hasLength(item.answer, 1, 1200))
            add(`${root}.items.${index}.answer`, "Every FAQ needs an answer.", block.id);
        });
        break;
    }
  });

  Object.entries(document.theme.overrides).forEach(([key, value]) => {
    if (value && !/^#[0-9a-f]{6}$/i.test(value))
      add(`theme.overrides.${key}`, "Use a six-digit hex colour.");
  });
  if (document.theme.settings) {
    const settings = resolvePublicProfileThemeSettings(document.theme);
    if (settings.backgroundImageUrl && !isHttpUrl(settings.backgroundImageUrl)) {
      add("theme.settings.backgroundImageUrl", "Enter a complete HTTP or HTTPS URL.");
    }
    (
      [
        "backgroundGradientEnd",
        "blockBorderColor",
        "highlightBackground",
        "highlightBorderColor",
        "surfaceBackground",
        "surfaceBorderColor",
      ] as const
    ).forEach((key) => {
      if (!/^#[0-9a-f]{6}$/i.test(settings[key]))
        add(`theme.settings.${key}`, "Use a six-digit hex colour.");
    });
  }

  (
    ["canonicalUrl", "openGraphImageUrl", "faviconUrl", "appleTouchIconUrl"] as const
  ).forEach((key) => {
    if (!isOptionalHttpUrl(document.seo[key]))
      add(`seo.${key}`, "Enter a complete HTTP or HTTPS URL.");
  });
  if (document.seo.title && !hasLength(document.seo.title, 0, 70))
    add("seo.title", "Use at most 70 characters.");
  if (document.seo.description && !hasLength(document.seo.description, 0, 160))
    add("seo.description", "Use at most 160 characters.");
  if (document.footer.copyright && !hasLength(document.footer.copyright, 0, 160))
    add("footer.copyright", "Use at most 160 characters.");

  if (new Blob([JSON.stringify(document)]).size > 256 * 1024)
    add("document", "This page is larger than the 256 KB publishing limit.");
  return issues;
}

export function createDefaultPublicProfileDocument(): PublicProfileDocument {
  return {
    schemaVersion: 1,
    header: {
      layout: "basic-left",
      showCoverImage: true,
      showAvatar: true,
      showBio: true,
      showLocation: true,
      showProfessionalTitles: true,
      showYearsExperience: true,
      showSocialLinks: true,
      maximumSocialLinks: 5,
    },
    blocks: [
      {
        id: "legacy-services",
        type: "offeringCollection",
        visible: true,
        title: "1:1 Sessions",
        description: null,
        collection: "services",
        source: {mode: "automatic"},
        limit: 24,
        layout: "grid",
        mobileColumns: 1,
        buttonLabel: "Book now",
      },
      {
        id: "legacy-live-events",
        type: "offeringCollection",
        visible: true,
        title: "Live events",
        description: null,
        collection: "liveEvents",
        source: {mode: "automatic"},
        limit: 24,
        layout: "grid",
        mobileColumns: 1,
        buttonLabel: "Reserve a spot",
      },
      {
        id: "legacy-store",
        type: "offeringCollection",
        visible: true,
        title: "Store",
        description: null,
        collection: "store",
        source: {mode: "automatic"},
        limit: 24,
        layout: "grid",
        mobileColumns: 1,
        buttonLabel: "Buy now",
      },
    ],
    theme: {
      preset: "mono",
      settings: {...PUBLIC_PROFILE_THEMES[0].settings},
      overrides: {
        pageBackground: null,
        surface: null,
        surfaceMuted: null,
        text: null,
        textMuted: null,
        accent: null,
        accentText: null,
      },
    },
    seo: {
      title: null,
      description: null,
      canonicalUrl: null,
      openGraphImageUrl: null,
      faviconUrl: null,
      appleTouchIconUrl: null,
      noIndex: false,
    },
    footer: {showCoachliBranding: true, copyright: null},
  };
}

export function reorderPublicProfileBlocks(
  blocks: PublicProfileBlock[],
  orderedBlockIds: string[]
): PublicProfileBlock[] {
  if (blocks.length !== orderedBlockIds.length) return blocks;

  const blocksById = new Map(blocks.map((block) => [block.id, block]));
  if (blocksById.size !== blocks.length) return blocks;

  const reordered = orderedBlockIds.map((id) => blocksById.get(id));
  if (
    new Set(orderedBlockIds).size !== blocks.length ||
    reordered.some((block) => !block)
  ) {
    return blocks;
  }

  return reordered as PublicProfileBlock[];
}
